import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICES } from "@/data/content";
import { SectionHeader } from "@/components/ui-bits";
import { staggerParent, staggerChild } from "@/components/motion-primitives";

const ServiceCard = ({ s, index }) => {
  const Icon = s.icon;
  return (
    <motion.article
      variants={staggerChild}
      data-testid={`service-card-${s.id}`}
      className="group relative flex flex-col rounded-2xl border border-slate-200/80 bg-white p-7 md:p-8 shadow-[0_2px_8px_rgba(15,23,42,0.04)] hover:shadow-[0_24px_60px_-24px_rgba(15,23,42,0.25)] hover:-translate-y-1.5 transition-all duration-400 overflow-hidden"
    >
      <div className="absolute -right-8 -top-8 w-28 h-28 rounded-full bg-secondary/[0.06] group-hover:bg-secondary/[0.12] transition-colors" />
      <div className="relative flex items-start justify-between">
        <span className="flex items-center justify-center w-14 h-14 rounded-xl bg-primary text-white group-hover:bg-secondary transition-colors duration-300">
          <Icon className="w-6 h-6" />
        </span>
        <span className="font-mono text-xs text-slate-300">0{index + 1}</span>
      </div>

      <h3 className="relative mt-6 font-heading text-xl font-semibold text-primary leading-snug">{s.title}</h3>
      <p className="relative mt-3 text-sm text-muted-foreground leading-relaxed">{s.blurb}</p>

      <ul className="relative mt-5 space-y-1.5 flex-1">
        {s.points.slice(0, 6).map((p, i) => (
          <li key={i} className="flex items-start gap-2 text-[13px] text-slate-600">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-secondary shrink-0" />
            {p}
          </li>
        ))}
      </ul>

      <a
        href={s.ctaHref || "#quote"}
        data-testid={`service-cta-${s.id}`}
        className="relative mt-7 inline-flex items-center gap-2 text-sm font-semibold text-primary group-hover:text-secondary transition-colors"
      >
        {s.cta}
        <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </a>
    </motion.article>
  );
};

export const Services = () => (
  <section id="services" className="relative py-24 md:py-32 bg-background">
    <div className="container mx-auto px-4 md:px-8">
      <SectionHeader overline="Our Services" title="What we can do for you" subtitle="Professional support across every municipal account and utility challenge you face." />
      <motion.div
        variants={staggerParent} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
        className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7"
      >
        {SERVICES.map((s, i) => <ServiceCard key={s.id} s={s} index={i} />)}

        <motion.a
          variants={staggerChild}
          href="#quote"
          data-testid="service-card-cta"
          className="group relative flex flex-col justify-between rounded-2xl bg-brand-dark grain p-8 text-white overflow-hidden min-h-[240px]"
        >
          <div className="dot-grid absolute inset-0 opacity-10" />
          <div className="relative">
            <span className="overline text-secondary">Not sure where to start?</span>
            <h3 className="mt-4 font-heading text-2xl font-medium leading-snug">Tell us about your municipal issue and we&rsquo;ll handle the rest.</h3>
          </div>
          <span className="relative mt-6 inline-flex items-center gap-2 font-semibold text-secondary">
            Request a Quote
            <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </span>
        </motion.a>
      </motion.div>
    </div>
  </section>
);
